import * as React from "react";
import { css, ClassNames } from "@emotion/core";
import turfArea from "@turf/area";
import turfLength from "@turf/length";
import {
  Button,
  Dialog,
  Navbar,
  NavbarGroup,
  NavbarHeading,
  EditableText,
  MenuItem,
  Icon,
  Tag,
  Tooltip,
  Position,
  FormGroup,
  InputGroup,
} from "@blueprintjs/core";
import { DateInput, TimePrecision, TimePicker } from "@blueprintjs/datetime";
import {
  utcToZonedTime,
  zonedTimeToUtc,
  getTimezoneOffset,
  format as formatInTimeZone,
} from "date-fns-tz";
import { format } from "date-fns";
import { Select } from "@blueprintjs/select";
import AssetInspector from "./AssetInspector";
import InspectorHeader from "./InspectorHeader";
import AnnotationLayerInspector from "./AnnotationLayerInspector";
import ApiAnnotations from "../../../api/annotations";
import {
  useSinkState,
  useUnitState,
  useUnitActions,
  useSinkActions,
  useAnnotationSubscription,
} from "context/";
import { formatWGS84 } from "utils";
import { CheckEditAccess } from "context/";
import { addMilliseconds } from "date-fns";

interface IMaterial {
  id: number;
  name: string;
}

interface IMeasurement {
  label: string;
  unit: string;
}

interface IUnit {
  id: number;
  unit: string;
}

const MaterialSelect = Select.ofType<IMaterial>();
const MeasurementSelect = Select.ofType<IMeasurement>();
const UnitSelect = Select.ofType<IUnit>();

const AddCustomMeasurement = (props) => {
  const { inspectorModel } = useSinkState();
  const { annotations } = inspectorModel.layer;

  const custom = annotations.filter(
    (annotation) => annotation.user_measurements?.length > 0
  );

  const properties = {};

  for (const annotation of custom) {
    for (const property of annotation.user_measurements) {
      properties[property.label] = property.unit;
    }
  }

  const m = inspectorModel.user_measurements
    ? inspectorModel.user_measurements.map(({ label }) => label)
    : [];

  const existing = Object.entries(properties)
    .map(([key, value]) => ({
      label: key,
      unit: value,
    }))
    .filter(({ label }) => m.includes(label) === false);

  return existing.length > 0 ? (
    <MeasurementSelect
      items={[...existing, { label: "Create New Property", unit: null }]}
      popoverProps={{
        minimal: true,
      }}
      itemRenderer={(item, { handleClick, modifiers }) => {
        return (
          <MenuItem
            css={css`
              width: 240px;
              margin-right: 0.25rem;
              background-color: ${modifiers.active ? "#137cbd" : "transparent"};
            `}
            key={item.label}
            text={item.unit ? `${item.label} (${item.unit})` : item.label}
            onClick={handleClick}
          />
        );
      }}
      onItemSelect={(item) => {
        if (item.label === "Create New Property") {
          props.setShowCustomMeasurementDialog(true);
        } else {
          props.setMeasurements((prev) => [
            ...prev,
            { label: item.label, unit: item.unit },
          ]);
          props.setShowCustomMeasurementDialog(
            inspectorModel.user_measurements
              ? inspectorModel.user_measurements.length
              : 0
          );
        }
      }}
    >
      <Button text="Add Property Row" rightIcon="double-caret-vertical" fill />
    </MeasurementSelect>
  ) : (
    <Button
      text="Add Property Row"
      rightIcon="double-caret-vertical"
      fill
      onClick={() => props.setShowCustomMeasurementDialog(true)}
    />
  );
};

const CustomMeasurementDialog = (props) => {
  const { getLayers, setAllowAnnotationArrowKeyNavigation } = useSinkActions();
  const { inspectorModel } = useSinkState();
  const { unit, conversions } = useUnitState();
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const {
    sink: { surveys },
    selectedSurveyId,
  } = useSinkState();
  const survey = surveys.find((survey) => survey.id === selectedSurveyId);

  if (!survey) {
    return null;
  }

  const [date, setDate] = React.useState(
    utcToZonedTime(
      new Date(
        props.editing && props.editing.time
          ? props.editing.time
          : survey.nominal_flight_date
      ),
      survey.timezone
    )
  );

  const adjustTime = (time: Date) => {
    time.setFullYear(date.getFullYear());
    time.setMonth(date.getMonth());
    time.setDate(date.getDate());

    return time;
  };

  const setDefaultTime = (time: Date) => {
    const offset = getTimezoneOffset(survey.timezone, time);

    time.setUTCHours(12 - offset / 3600000);
    time.setUTCMinutes(0);
    time.setUTCSeconds(0);
    time.setUTCMilliseconds(0);

    return time;
  };

  const [time, setTime] = React.useState(
    props.editing && props.editing.time
      ? utcToZonedTime(
          adjustTime(new Date(props.editing.time)),
          survey.timezone
        )
      : utcToZonedTime(setDefaultTime(date), survey.timezone)
  );

  const [timeChanged, setTimeChanged] = React.useState(
    props.editing ? props.editing.time !== undefined : false
  );

  const supported = React.useMemo(
    () =>
      conversions
        .filter((unit) => ["mass_dry"].includes(unit.type))
        .map(({ type, label, imperial_unit, metric_unit }) =>
          unit === "sae"
            ? {
                id: type,
                label: props.editing?.label || label,
                unit: imperial_unit,
              }
            : {
                id: type,
                label: props.editing?.label || label,
                unit: metric_unit,
              }
        ),
    [conversions, unit]
  );

  const [selectedUnit, setSelectedUnit] = React.useState(
    props.editing ? supported.find((u) => u.id === props.editing.unit) : null
  );

  const applyDate = React.useCallback(
    (date) => {
      const t = time || new Date();

      const hours = t.getHours();
      const minutes = t.getMinutes();
      const seconds = t.getSeconds();

      const dateWithTime = new Date(date);

      dateWithTime.setHours(hours);
      dateWithTime.setMinutes(minutes);
      dateWithTime.setSeconds(seconds);

      return dateWithTime;
    },
    [time]
  );

  const [customMeasurementErrors, setCustomMeasurementErrors] = React.useState(
    []
  );

  const close = () => {
    if (saving || deleting) return;
    props.close();
  };

  const convertToSiteTime = React.useCallback(
    (date) => {
      if (!date) return null;

      const siteOffset = getTimezoneOffset(survey.timezone, date);
      const localOffset = getTimezoneOffset(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        date
      );

      /* Date is displayed in the users local timezone, but it represents that time in the survey timezone.
         We add the difference betweent the users current timezone and their survey timezone, then convert it to UTC.
      */
      const offsetDate = addMilliseconds(date, localOffset - siteOffset);

      return zonedTimeToUtc(
        offsetDate,
        Intl.DateTimeFormat().resolvedOptions().timeZone
      );
    },
    [survey.timezone]
  );

  React.useEffect(() => {
    setAllowAnnotationArrowKeyNavigation(false);
    return () => setAllowAnnotationArrowKeyNavigation(true);
  }, []);

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 524px;
      `}
      title={
        confirmDelete
          ? "Delete Custom Property"
          : props.editing
          ? "Edit Custom Property"
          : "Add Property"
      }
      isOpen
      onClose={close}
    >
      {confirmDelete ? (
        <div
          css={css`
            padding: 1rem;
          `}
        >
          <p
            css={css`
              margin: 1rem 0;
            `}
          >
            Are you sure you want to delete this property?
          </p>
          <div
            css={css`
              display: flex;
              justify-content: end;
              gap: 1rem;
            `}
          >
            <Button
              text="Cancel"
              disabled={deleting}
              intent="none"
              onClick={() => setConfirmDelete(false)}
            />
            <Button
              text={deleting ? "Deleting..." : "Confirm"}
              disabled={deleting}
              intent="danger"
              onClick={async () => {
                setDeleting(true);
                await ApiAnnotations.deleteUserMeasurement(
                  inspectorModel.id,
                  props.index
                );
                await getLayers(inspectorModel.layer.survey_id);
                setDeleting(false);
                close();
              }}
            />
          </div>
        </div>
      ) : (
        <form
          css={css`
            padding: 1rem;
          `}
          onSubmit={async (event) => {
            event.preventDefault();

            const label = selectedUnit.label;
            const value = event.currentTarget.elements.value.value;
            const unit = selectedUnit.id;

            const errors = [];
            const lookup = {
              label,
              value,
              unit,
              date,
              time,
            } as const;

            for (const key of [
              "label",
              "value",
              "unit",
              "date",
              "time",
            ] as const) {
              if (key === "value") {
                if (!lookup[key] || isNaN(Number(lookup[key]))) {
                  errors.push(key);
                }
              } else if (key === "time") {
                if (timeChanged === false) {
                  errors.push(key);
                }
              } else {
                if (!lookup[key]) {
                  errors.push(key);
                }
              }
            }

            setCustomMeasurementErrors(errors);

            if (errors.length === 0) {
              setSaving(true);
              if (props.editing) {
                await ApiAnnotations.updateUserMeasurement(
                  inspectorModel.id,
                  props.index,
                  {
                    label,
                    value,
                    unit,
                    time: convertToSiteTime(time),
                  }
                );
              } else {
                await ApiAnnotations.createUserMeasurement(inspectorModel.id, {
                  label,
                  value,
                  unit,
                  time: convertToSiteTime(time),
                });
              }

              await getLayers(inspectorModel.layer.survey_id);

              setSaving(false);
              props.close();
            }
          }}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
            `}
          >
            <FormGroup
              label="Label"
              labelFor="create-measurement-label"
              labelInfo="(required)"
            >
              <UnitSelect
                items={supported}
                filterable={false}
                popoverProps={{
                  minimal: true,
                }}
                itemRenderer={(item, { handleClick, modifiers }) => {
                  return (
                    <MenuItem
                      css={css`
                        width: 240px;
                        margin-right: 0.25rem;
                        background-color: ${modifiers.active
                          ? "#137cbd"
                          : "transparent"};
                      `}
                      key={item.id}
                      text={`${item.label} (${item.unit})`}
                      onClick={handleClick}
                    />
                  );
                }}
                onItemSelect={() => null}
                activeItem={selectedUnit}
                onActiveItemChange={setSelectedUnit}
              >
                <Button
                  text={
                    selectedUnit
                      ? `${selectedUnit.label} (${selectedUnit.unit})`
                      : "Label"
                  }
                  endIcon="double-caret-vertical"
                  fill
                />
              </UnitSelect>
            </FormGroup>
            <FormGroup
              label="Value"
              labelFor="create-measurement-value"
              labelInfo="(required)"
            >
              <InputGroup
                id="create-measurement-value"
                defaultValue={props.editing ? props.editing.value : ""}
                name="value"
                autoFocus={props.editing !== null}
              />
            </FormGroup>
          </div>
          <div
            css={css`
              width: 50%;
            `}
          >
            <FormGroup
              disabled={false}
              label="Date of Measurement"
              labelInfo="(required)"
              css={css`
                margin-right: 1rem;
              `}
            >
              <DateInput
                closeOnSelection
                canClearSelection={false}
                formatDate={(date) => format(date, "yyyy-MM-dd")}
                parseDate={(date) => {
                  if (date) {
                    return applyDate(date);
                  }

                  return null;
                }}
                onChange={(date) => setDate(applyDate(date))}
                placeholder={"YYYY-MM-DD"}
                value={date}
                popoverProps={{ position: Position.TOP }}
                fill
              />
            </FormGroup>
            <FormGroup
              disabled={false}
              label="Time of Measurement"
              labelInfo="(required)"
              helperText={formatInTimeZone(
                utcToZonedTime(date, survey.timezone),
                "zzzz XXX",
                {
                  timeZone: survey.timezone,
                }
              )}
              css={css`
                margin-right: 1rem;
              `}
            >
              <TimePicker
                value={time}
                onChange={(time) => {
                  setTimeChanged(true);
                  setTime(adjustTime(time));
                }}
                autoFocus={false}
                disabled={false}
                precision={TimePrecision.MINUTE}
                selectAllOnFocus={false}
                showArrowButtons={false}
                useAmPm={true}
              />
            </FormGroup>
          </div>
          <div
            css={css`
              display: flex;
              justify-content: between;
              align-items: center;
              width: 100%;
            `}
          >
            <div
              css={css`
                color: var(--danger);
                flex-grow: 1;
              `}
            >
              {customMeasurementErrors.length > 0 ? (
                `Missing fields: ${customMeasurementErrors.join(", ")}.`
              ) : (
                <Button
                  text={deleting ? "Deleting..." : "Delete"}
                  intent="danger"
                  disabled={saving || deleting}
                  onClick={() => setConfirmDelete(true)}
                />
              )}
            </div>
            <div
              css={css`
                display: flex;
                justify-content: end;
                gap: 1rem;
              `}
            >
              <Button
                text="Cancel"
                disabled={saving || deleting}
                intent="none"
                onClick={close}
              />
              <Button
                text={saving ? "Saving" : "Save"}
                disabled={saving || deleting}
                intent="primary"
                type="submit"
              />
            </div>
          </div>
        </form>
      )}
    </Dialog>
  );
};

const getMeasurements = (geometry) => {
  if (geometry.type === "Polygon") {
    return [
      { label: "Polygon Area", value: turfArea(geometry), unit: "area_2D" },
      {
        label: "Polygon Perimeter",
        value: turfLength(geometry) * 1000,
        unit: "length",
      },
    ];
  }

  if (geometry.type === "LineString") {
    return [
      {
        label: "Line Length",
        value: turfLength(geometry) * 1000,
        unit: "length",
      },
    ];
  }

  if (geometry.type === "Point") {
    const { latitude, longitude } = formatWGS84(geometry.coordinates);

    return [
      {
        label: "Coordinates",
        value: `${latitude}, ${longitude}`,
        unit: "WGS84",
      },
    ];
  }

  return [];
};

const LiveMeasurements = ({
  annotation,
  userMeasurements,
  setShowCustomMeasurementDialog,
  surveyLocked,
}) => {
  const { convert } = useUnitActions();
  const { liveGeometry } = useAnnotationSubscription(annotation);

  const convertPropertyUnits = (list) =>
    list.map((item) => ({
      ...item,
      value: item.unit ? convert(item.value, item.unit) : item.value,
    }));

  return (
    <AssetInspector
      data={[
        ...convertPropertyUnits(
          annotation.layer.source_type === null
            ? getMeasurements(liveGeometry ?? annotation.geometry)
            : annotation.properties
        ),
        ...userMeasurements,
      ]}
      raw={[
        ...(annotation.properties ? annotation.properties : []),
        ...userMeasurements,
      ]}
      setShowCustomMeasurementDialog={setShowCustomMeasurementDialog}
      surveyLocked={surveyLocked}
    />
  );
};

const AnnotationInspector = ({ annotation, closeInspector }) => {
  const {
    sink,
    inspectorShowMaterial,
    selectedModelId,
    surveys,
    selectedSurveyId,
  } = useSinkState();
  const { setAllowAnnotationArrowKeyNavigation } = useSinkActions();
  const survey = surveys.find((survey) => survey.id === selectedSurveyId);

  const { conversions } = useUnitState();
  const materialSelectRef = React.useRef(null);

  const material = sink.materials.find(
    (material) => material.id === annotation.material_id
  );

  const [selectedMaterial, setSelectedMaterial] = React.useState(
    material ? material.name : ""
  );
  const [materialQuery, setMaterialQuery] = React.useState("");

  const [description, setDescription] = React.useState(
    annotation.description || ""
  );

  const [showCustomMeasurementDialog, setShowCustomMeasurementDialog] =
    React.useState(false);

  const [measurements, setMeasurements] = React.useState([]);

  React.useEffect(() => {
    if (
      materialSelectRef.current &&
      (inspectorShowMaterial > 0 || materialQuery) &&
      annotation.material_id === null &&
      selectedModelId === null
    ) {
      materialSelectRef.current.setState({ isOpen: true });
    }
  }, [
    annotation.id,
    annotation.material_id,
    inspectorShowMaterial,
    selectedModelId,
  ]);

  React.useEffect(() => {
    setDescription(annotation.description || "");
  }, [annotation.description]);

  React.useEffect(() => {
    if (annotation.material_id) {
      const material = sink.materials.find(
        (material) => material.id === annotation.material_id
      );

      if (material) {
        setSelectedMaterial(material.name);
        return;
      }
    }

    setSelectedMaterial("");
  }, [annotation.material_id, sink.materials]);

  const userMeasurements = [
    ...(annotation.user_measurements || []).map((measurement, index) => ({
      ...measurement,
      label:
        measurement.label ||
        conversions.find(({ type }) => type === measurement.unit)?.label ||
        "",
      editable: true,
      index,
    })),
    ...measurements.map((measurement, index) => ({
      ...measurement,
      index:
        index +
        (annotation.user_measurements
          ? annotation.user_measurements.length - 1
          : 0),
    })),
  ];

  return (
    <>
      <AnnotationLayerInspector
        layer={annotation.layer}
        closeInspector={closeInspector}
      />
      <div
        css={css`
          padding: 0;
        `}
      >
        <div
          css={css`
            margin-top: 2px;
          `}
        >
          <InspectorHeader
            model={annotation}
            sub
            onDelete={
              annotation.editable
                ? () =>
                    annotation.api
                      .archive()
                      .then((archived) => {
                        if (archived) closeInspector();
                      })
                : null
            }
          />
        </div>
        {annotation.source_id && (
          <div
            css={css`
              padding: 0;
              font-size: 0.667rem;
              font-weight: 700;
              color: var(--neutral-4);
              display: flex;
              align-items: center;
              margin-top: 1rem;
            `}
          >
            <Tooltip
              position={Position.LEFT}
              content={
                <div
                  css={css`
                    max-width: 45ch;
                  `}
                >
                  <h3>UID</h3>
                  <p>
                    A unique identifier is associated with a single measurement
                    or markup.
                  </p>
                  {annotation.reference_id && (
                    <>
                      <br />
                      <h3>RefID</h3>
                      <p>
                        A report reference identifier is produced for
                        measurements when reports are made. It can be used to
                        help reference between the downloadable reports and the
                        FYBR Cloud.
                      </p>
                    </>
                  )}
                </div>
              }
            >
              <Icon
                icon="info-sign"
                css={css`
                  padding: 0 0.5rem 0 1rem;
                `}
              />
            </Tooltip>
            <div>
              <div>{`UID: ${annotation.id}`}</div>
              {annotation.reference_id && (
                <div>{`RefID: ${annotation.reference_id}`}</div>
              )}
            </div>
          </div>
        )}
        <div
          css={css`
            padding: 1rem;
            position: relative;
            height: 62px;
          `}
        >
          <div
            css={css`
              visibility: ${selectedMaterial ? "visible" : "hidden"};
              position: absolute;
            `}
          >
            <CheckEditAccess
              fallback={
                <Tag size="large" fill icon="cube">
                  {selectedMaterial}
                </Tag>
              }
              checkLocked
            >
              <Tag
                size="large"
                fill
                icon="cube"
                onRemove={() => {
                  setSelectedMaterial("");
                  ApiAnnotations.updateAnnotation(annotation.id, {
                    material_id: null,
                  });
                }}
              >
                {selectedMaterial}
              </Tag>
            </CheckEditAccess>
          </div>
          <div
            css={css`
              visibility: ${selectedMaterial ? "hidden" : "visible"};
              position: absolute;
            `}
          >
            <CheckEditAccess
              fallback={
                <span
                  css={css`
                    color: var(--neutral-4);
                  `}
                >
                  No material selected
                </span>
              }
              checkLocked={annotation.source_id !== null}
            >
              <MaterialSelect
                key={annotation.id}
                ref={materialSelectRef}
                items={sink.materials
                  .filter(
                    (material) =>
                      material.archived === null ||
                      material.id === annotation.material_id
                  )
                  .filter((material) =>
                    materialQuery.length > 0
                      ? material.name
                          .toLowerCase()
                          .includes(materialQuery.toLowerCase())
                      : true
                  )}
                popoverProps={{
                  minimal: true,
                  onOpening: () => setAllowAnnotationArrowKeyNavigation(false),
                  onClosed: () => setAllowAnnotationArrowKeyNavigation(true),
                }}
                query={materialQuery}
                onQueryChange={(query, event) => setMaterialQuery(query)}
                itemRenderer={(item, { handleClick, modifiers }) => {
                  return (
                    <MenuItem
                      css={css`
                        width: 240px;
                        margin-right: 0.25rem;
                        background-color: ${modifiers.active
                          ? "#137cbd"
                          : "transparent"};
                      `}
                      key={item.name}
                      text={item.name}
                      onClick={handleClick}
                    />
                  );
                }}
                onItemSelect={(item) => {
                  setSelectedMaterial(item.name);
                  ApiAnnotations.updateAnnotation(annotation.id, {
                    material_id: item.id,
                  });
                }}
              >
                <Button
                  text="Add Material"
                  endIcon="double-caret-vertical"
                  fill
                />
              </MaterialSelect>
            </CheckEditAccess>
          </div>
        </div>
      </div>
      <div>
        <Navbar>
          <NavbarGroup>
            <NavbarHeading>Asset Description</NavbarHeading>
          </NavbarGroup>
        </Navbar>
        <CheckEditAccess
          fallback={
            description ? (
              <pre
                css={css`
                  padding: 1rem;
                  max-height: 228px;
                  overflow-y: auto;
                  font-family: inherit;
                  white-space: pre-wrap;
                  word-break: break-word;
                `}
              >
                {description}
              </pre>
            ) : (
              <div
                css={css`
                  color: var(--neutral-4);
                  padding: 1rem;
                `}
              >
                No description added
              </div>
            )
          }
          checkLocked={annotation.source_id !== null}
        >
          <ClassNames>
            {({ css }) => (
              <EditableText
                selectAllOnFocus
                confirmOnEnterKey
                multiline
                minLines={6}
                maxLines={12}
                placeholder="Add Description"
                value={description}
                onChange={setDescription}
                onConfirm={(description) => {
                  if (description !== annotation.description) {
                    ApiAnnotations.updateAnnotation(annotation.id, {
                      description:
                        description.trim() === "" ? null : description,
                    });
                  }
                }}
                className={css`
                  border: 0;

                  &::before {
                    display: none;
                  }

                  & * {
                    padding: 1rem 0 1rem 1rem;
                    max-height: 228px;
                  }

                  & span:hover {
                    background-color: var(--midnight);
                    cursor: text;
                  }
                `}
              />
            )}
          </ClassNames>
        </CheckEditAccess>
      </div>
      <div
        css={css`
          margin: 0;
        `}
      >
        <Navbar>
          <NavbarGroup>
            <NavbarHeading>Asset Properties</NavbarHeading>
          </NavbarGroup>
        </Navbar>
        <LiveMeasurements
          annotation={annotation}
          userMeasurements={userMeasurements}
          setShowCustomMeasurementDialog={setShowCustomMeasurementDialog}
          surveyLocked={survey?.locked_by_id != null}
        />
      </div>

      {showCustomMeasurementDialog !== false && (
        <CustomMeasurementDialog
          editing={
            typeof showCustomMeasurementDialog === "number"
              ? userMeasurements[showCustomMeasurementDialog]
              : null
          }
          index={showCustomMeasurementDialog}
          close={() => {
            setShowCustomMeasurementDialog(false);
            setMeasurements([]);
          }}
        />
      )}
    </>
  );
};

export default AnnotationInspector;
